import type { Map, OrderedMap } from '@gonfalon/immutable-shim/immutableShim';
// oxlint-disable-next-line no-restricted-imports
import { fromJS } from '@gonfalon/immutable-shim/immutableShim';
import type { CreateFunctionInput, ImmutableMap } from '@gonfalon/legacy-immutable/immutableUtils';
import { normalize } from 'normalizr';

import { convertMapToOrderedMap } from '#utils/collectionUtils';
import type { Environment } from '#utils/environments/records/Environment';
import { createEnvironment } from '#utils/environments/records/Environment';
import { formatPaginatedProjects } from '#utils/projects/formatPaginatedProjects';
import { normalizeProject } from '#utils/projects/normalize';
import type { NormalizedProjectResponse, RawProjectPaginatedRep, RawProjectRep } from '#utils/projects/projectTypes';
import type { Project } from '#utils/projects/records/Project';
import { createProject } from '#utils/projects/records/Project';
import type { ProjectWithEnvironmentEntities } from '#utils/projects/records/ProjectWithEnvironmentEntities';
import { environmentSchema, projectSchema } from '#utils/projects/schemas';

export function processPaginatedProjectCollectionRep(rep: {
  items: Array<CreateFunctionInput<RawProjectPaginatedRep>>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  _links?: { [k: string]: any };
}) {
  const normalized = normalize({ ...rep, items: formatPaginatedProjects(rep.items) }, { items: [projectSchema] });
  return fromJS(normalized).withMutations(
    (
      map: ImmutableMap<{
        entities: {
          projects: Map<string, CreateFunctionInput<Project>>;
          environments: Map<string, CreateFunctionInput<Environment>>;
          environmentsTotalCount: number;
        };
      }>,
    ) => {
      map.updateIn(['entities', 'projects'], (projects) => convertMapToOrderedMap(projects, normalized.result.items));
      map.updateIn(['entities', 'projects'], (projects) => projects.reverse().map(createProject));
      map.updateIn(['entities', 'environments'], (envs) => envs && envs.map(createEnvironment));
    },
  );
}

export function processProjectCollectionRep(rep: {
  items: Array<CreateFunctionInput<RawProjectRep>>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  _links?: { [k: string]: any };
}) {
  const normalized = normalize(rep, { items: [projectSchema] });
  return fromJS(normalized).withMutations(
    (
      map: ImmutableMap<{
        entities: {
          projects: Map<string, CreateFunctionInput<Project>>;
          environments: Map<string, CreateFunctionInput<Environment>>;
        };
      }>,
    ) => {
      map.updateIn(['entities', 'projects'], (projects) => convertMapToOrderedMap(projects, normalized.result.items));
      map.updateIn(['entities', 'projects'], (projects) => projects.reverse().map(createProject));
      map.updateIn(['entities', 'environments'], (envs) => envs && envs.map(createEnvironment));
    },
  );
}

// TODO: Move all environment related utilities to environmentUtils.ts
export function processProjectEnvironmentsCollection(res: {
  totalCount?: number;
  items: Environment[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  _links?: { [k: string]: any };
}) {
  const normalized = normalize(res, { items: [environmentSchema] });
  return fromJS(normalized).withMutations(
    (
      map: ImmutableMap<{
        entities: {
          environments: OrderedMap<string, CreateFunctionInput<Environment>>;
        };
      }>,
    ) => {
      map.updateIn(['entities', 'environments'], (envs) => convertMapToOrderedMap(envs, normalized.result.items));
      map.updateIn(['entities', 'environments'], (envs) => envs && envs.map(createEnvironment));
    },
  );
}

export function createNormalizedProjectEntity(
  rep: CreateFunctionInput<ProjectWithEnvironmentEntities>,
): NormalizedProjectResponse {
  const data = normalizeProject(rep);
  return fromJS(data).withMutations(
    (
      map: ImmutableMap<{
        result: Project;
        entities: Map<string, { environments: Map<string, Environment> }>;
      }>,
    ) => {
      map.update('result', createProject);
      map.updateIn(['entities', 'environments'], (envs) => envs.map(createEnvironment));
    },
  );
}

export function processLoadedProjectRep(rep: CreateFunctionInput<ProjectWithEnvironmentEntities>) {
  return createNormalizedProjectEntity(rep);
}
